import {
  forwardRef,
  useRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ElementType,
  type KeyboardEvent,
  type MouseEvent,
  type MouseEventHandler,
  type MutableRefObject,
  type ReactNode,
  type Ref,
} from 'react';
import classNames from 'classnames';
import { Icon, IconName, IconSize } from '@components/icon';
import './Button.scss';

export const BUTTON_COLOR_SCHEMES = ['primary', 'secondary'] as const;
export const BUTTON_SIZES = ['medium', 'large', 'xl'] as const;
export const BUTTON_VARIANTS = ['outlined', 'solid'] as const;

export type ButtonColorScheme = (typeof BUTTON_COLOR_SCHEMES)[number];
export type ButtonSize = (typeof BUTTON_SIZES)[number];
export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];
export type ButtonTypes = 'button' | 'submit' | 'reset' | undefined;

/** Icon sizes supported by `Button` (excludes `xl` and `xxl`, which overflow button bounds). */
export type ButtonIconSize = Exclude<IconSize, 'xl' | 'xxl'>;

const DEFAULT_ICON_SIZE: ButtonIconSize = 'small';

/**
 * Props `Button` forwards to the polymorphic link root (`linkComponent`, default native `a`).
 */
export interface ButtonForwardedLinkProps
  extends Pick<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    'className' | 'target' | 'tabIndex' | 'aria-disabled' | 'aria-label'
  > {
  href: string;
  onClick?: MouseEventHandler<Element>;
  children: ReactNode;
}

interface ButtonSharedProps {
  endIcon?: IconName;
  /** Icon size for `endIcon`. Defaults to `small` (16px). */
  endIconSize?: ButtonIconSize;
  children: ReactNode;
  colorScheme?: ButtonColorScheme;
  size?: ButtonSize;
  startIcon?: IconName;
  /** Icon size for `startIcon`. Defaults to `small` (16px). */
  startIconSize?: ButtonIconSize;
  variant?: ButtonVariant;
  disabled?: boolean;
  className?: string;
  /** Custom component to render the link instead of a native `<a>` (e.g. Next.js Link). Must accept `href` as a prop. */
  linkComponent?: ElementType;
}

type ButtonAsButtonProps = ButtonSharedProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonSharedProps | 'children'> & {
    href?: undefined;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  };

type ButtonAsAnchorProps = ButtonSharedProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonSharedProps | 'children'> & {
    href: string;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    type?: never;
  };

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

/**
 * Button component. Renders a native `<button>` by default; pass `href` to render a visually identical anchor.
 * @param children - Button label content.
 * @param colorScheme - Color scheme. Default: `primary`.
 * @param size - Button size. Default: `large`.
 * @param variant - Visual variant. Default: `solid`.
 * @param startIcon - Optional leading icon name.
 * @param startIconSize - Icon size for `startIcon`. Default: `small`.
 * @param endIcon - Optional trailing icon name.
 * @param endIconSize - Icon size for `endIcon`. Default: `small`.
 * @param disabled - Disables interaction. Default: `false`.
 * @param type - Native button `type`. Default: `submit`. Ignored when `href` is set.
 * @param href - When set, renders an anchor instead of a button.
 * @param linkComponent - Custom component to render the link instead of a native `<a>` (e.g. Next.js Link). Must accept `href` as a prop. Defaults to `a`.
 * @param className - Additional class name.
 * @param onClick - Click handler.
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      children,
      colorScheme = 'primary',
      endIcon,
      endIconSize = DEFAULT_ICON_SIZE,
      onClick,
      size = 'large',
      startIcon,
      startIconSize = DEFAULT_ICON_SIZE,
      variant = 'solid',
      disabled,
      type = 'submit',
      className,
      href,
      linkComponent,
      ...rest
    },
    ref,
  ) => {
    const anchorRef = useRef<HTMLAnchorElement | null>(null);
    const classes = classNames(
      'vds-button',
      `vds-button-text-${size}`,
      `vds-button--${colorScheme}`,
      `vds-button--${size}`,
      `vds-button--${variant}`,
      { 'vds-button--disabled': disabled },
      className,
    );

    const content = (
      <>
        <div className="vds-button__border" />
        {startIcon ? (
          <span className="vds-button__icon vds-button__icon--start">
            <Icon name={startIcon} size={startIconSize} />
          </span>
        ) : null}

        <span className="vds-button__children">{children}</span>

        {endIcon ? (
          <span className="vds-button__icon vds-button__icon--end">
            <Icon name={endIcon} size={endIconSize} />
          </span>
        ) : null}
      </>
    );

    if (href) {
      const LinkWrapper = linkComponent || 'a';
      const { tabIndex, onKeyDown, ...anchorRest } = rest as Omit<
        AnchorHTMLAttributes<HTMLAnchorElement>,
        keyof ButtonSharedProps
      >;

      const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
          event.preventDefault();
          return;
        }

        (onClick as ButtonAsAnchorProps['onClick'])?.(event);
      };

      const handleAnchorKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
        onKeyDown?.(event);

        if (disabled || event.defaultPrevented) return;

        if (event.code === 'Space') {
          event.preventDefault();
          anchorRef.current?.click();
        }
      };

      const setAnchorRef = (node: HTMLAnchorElement | null) => {
        anchorRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as MutableRefObject<HTMLAnchorElement | null>).current = node;
        }
      };

      return (
        <LinkWrapper
          {...anchorRest}
          className={classes}
          ref={setAnchorRef}
          href={href}
          onClick={handleAnchorClick}
          onKeyDown={handleAnchorKeyDown}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : tabIndex}
          rel={
            anchorRest.rel ?? (anchorRest.target === '_blank' ? 'noopener noreferrer' : undefined)
          }>
          {content}
        </LinkWrapper>
      );
    }

    return (
      <button
        className={classes}
        ref={ref as Ref<HTMLButtonElement>}
        onClick={onClick as ButtonAsButtonProps['onClick']}
        disabled={disabled}
        type={type}
        {...(rest as Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonSharedProps>)}>
        {content}
      </button>
    );
  },
);

Button.displayName = 'Button';
